import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown, Loader2, X } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TdHTMLAttributes,
  type TextareaHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

/**
 * UI-primitiver i shadcn-stil, genereret lokalt ind i repoet som PRD par. 15.4 foreskriver.
 * De indeholder ingen forretningslogik - kun udseende og tilgaengelighed.
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors ' +
    'disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-white hover:bg-brand-700',
        secondary: 'border border-line bg-surface text-ink hover:bg-canvas',
        destructive: 'bg-danger text-white hover:bg-red-600',
        ghost: 'text-ink hover:bg-canvas',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-sm',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled ?? loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
});

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...props} />;
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-3 border-b border-line', className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-sm font-semibold', className)} {...props} />;
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4', className)} {...props} />;
}

/**
 * A card folded to its title until the title is clicked. The description and `actions` show only
 * while open; `children` - the body - is not drawn while folded.
 */
export function CollapsibleCard({
  title,
  description,
  actions,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <CardHeader
        className={cn('flex flex-wrap items-start justify-between gap-3', !open && 'border-b-0')}
      >
        <div className="min-w-0">
          <CardTitle>
            <button
              type="button"
              aria-expanded={open}
              className="flex items-center gap-1.5 text-left hover:text-brand"
              onClick={() => setOpen(!open)}
            >
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-ink-muted transition-transform',
                  !open && '-rotate-90',
                )}
                aria-hidden
              />
              {title}
            </button>
          </CardTitle>
          {open && description ? (
            <p className="mt-0.5 pl-[22px] text-xs text-ink-muted">{description}</p>
          ) : null}
        </div>
        {open ? actions : null}
      </CardHeader>
      {open ? children : null}
    </Card>
  );
}

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border',
  {
    variants: {
      tone: {
        neutral: 'bg-canvas text-ink-muted border-line',
        brand: 'bg-brand-50 text-brand-800 border-brand-100',
        success: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        warning: 'bg-amber-50 text-amber-900 border-amber-200',
        danger: 'bg-red-50 text-red-800 border-red-200',
        info: 'bg-blue-50 text-blue-800 border-blue-200',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export function Badge({
  className,
  tone,
  ...props
}: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

/**
 * Fields are 16 px below `sm` and 14 px above. iOS Safari zooms the whole page in when a field
 * under 16 px gets focus, and leaves it zoomed - on a phone every tap on a filter did that.
 */
const FIELD_TEXT = 'text-base sm:text-sm';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          'h-9 w-full rounded-md border border-line bg-surface px-3',
          FIELD_TEXT,
          'placeholder:text-ink-muted disabled:bg-canvas disabled:text-ink-muted',
          className,
        )}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'w-full rounded-md border border-line bg-surface px-3 py-2',
        FIELD_TEXT,
        'placeholder:text-ink-muted',
        className,
      )}
      rows={props.rows ?? 3}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          'h-9 w-full rounded-md border border-line bg-surface px-2',
          FIELD_TEXT,
          className,
        )}
        {...props}
      />
    );
  },
);

export interface ComboboxOption {
  value: string;
  label: string;
  /**
   * Saetter en prik foran valget.
   *
   * Hvad prikken *betyder* hoerer paa kaldstedet - se `markLabel`, som ogsaa er den tekst, en
   * skaermlaeser faar. Uden den ville prikken vaere en farve uden ord, og det er ikke en
   * oplysning for alle.
   */
  marked?: boolean;
}

/**
 * Aabne/lukke-adfaerden bag Combobox og MultiSelect.
 *
 * Begge skal lukke paa klik udenfor og paa Escape. Wiringen ligger ét sted, saa de to
 * ikke kan naa at opfoere sig forskelligt.
 */
function usePopover(): {
  open: boolean;
  setOpen: (open: boolean) => void;
  rootRef: React.RefObject<HTMLDivElement>;
} {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return { open, setOpen, rootRef };
}

/** Faelles udseende for de to dropdowns' knap. */
const triggerClass =
  'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-line ' +
  'bg-surface px-2 text-left text-sm disabled:bg-canvas disabled:text-ink-muted';

const panelClass = 'absolute z-40 mt-1 w-full rounded-md border border-line bg-surface shadow-lg';

/**
 * Dropdown hvor flere vaerdier kan vaelges. Knappen opsummerer valget, saa filterpanelet
 * fylder det samme uanset hvor mange der er krydset af.
 */
export function MultiSelect({
  values,
  onChange,
  options,
  allLabel = 'Alle',
}: {
  values: readonly string[];
  onChange: (values: string[]) => void;
  options: readonly ComboboxOption[];
  allLabel?: string;
}) {
  const { open, setOpen, rootRef } = usePopover();

  const toggle = (value: string): void => {
    onChange(
      values.includes(value) ? values.filter((current) => current !== value) : [...values, value],
    );
  };

  // Ét valg vises ved navn; flere ville ikke kunne staa paa knappen uden at klippe.
  const summary =
    values.length === 0
      ? allLabel
      : values.length === 1
        ? (options.find((option) => option.value === values[0])?.label ?? '1 valgt')
        : `${values.length} valgt`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={triggerClass}
      >
        <span className={cn('truncate', values.length === 0 && 'text-ink-muted')}>{summary}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
      </button>

      {open ? (
        <div className={panelClass}>
          <ul role="listbox" aria-multiselectable className="max-h-64 overflow-y-auto py-1">
            {options.map((option) => (
              <li key={option.value}>
                <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-canvas">
                  <input
                    type="checkbox"
                    checked={values.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
          {values.length > 0 ? (
            <div className="border-t border-line p-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full rounded px-2 py-1 text-left text-xs text-ink-muted hover:bg-canvas"
              >
                Ryd valg
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Prikken foran et markeret valg.
 *
 * `sr-only`-teksten er ikke valgfri: farven alene siger ingenting til den, der ikke kan se den,
 * og prikken er netop den oplysning, listen skal kunne skimmes paa.
 */
function Mark({ label }: { label?: string }) {
  return (
    <span className="flex shrink-0 items-center">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}

/**
 * Soegbar dropdown til filtre med for mange valg til en almindelig <select>.
 *
 * Listen kan vaere paa flere hundrede kontrakter, hvor en select tvinger brugeren til at
 * rulle. Soegefeltet er derfor det foerste der faar fokus naar panelet aabnes.
 *
 * Vaerdien er altid en af optionernes `value` eller den tomme streng - den fritekst der
 * skrives, filtrerer kun listen og bliver aldrig selv til et filter. Ellers kunne en
 * halvskrevet soegning se ud som et aktivt valg.
 */
export function Combobox({
  value,
  onChange,
  options,
  allLabel = 'Alle',
  searchPlaceholder = 'Søg',
  disabled,
  markLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly ComboboxOption[];
  allLabel?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Hvad prikken paa en `marked` option betyder. Vises som titel og laeses op. */
  markLabel?: string;
}) {
  const { open, setOpen, rootRef } = usePopover();
  const [term, setTerm] = useState('');

  const selected = options.find((option) => option.value === value);
  const needle = term.trim().toLowerCase();
  const matches = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options;

  const select = (next: string): void => {
    onChange(next);
    setOpen(false);
    setTerm('');
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setTerm('');
        }}
        className={triggerClass}
      >
        {/* Prikken staar ogsaa paa knappen, saa den ikke forsvinder i det oejeblik valget er truffet. */}
        <span className={cn('flex min-w-0 items-center gap-2', !selected && 'text-ink-muted')}>
          {selected?.marked ? <Mark label={markLabel} /> : null}
          <span className="truncate">{selected ? selected.label : allLabel}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
      </button>

      {open ? (
        <div className={panelClass}>
          <div className="p-1.5">
            <Input
              autoFocus
              className="h-8 sm:text-xs"
              placeholder={searchPlaceholder}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto pb-1 text-sm">
            <li>
              <button
                type="button"
                onClick={() => select('')}
                className={cn(
                  'w-full px-3 py-1.5 text-left hover:bg-canvas',
                  !selected && 'font-medium text-brand-800',
                )}
              >
                {allLabel}
              </button>
            </li>
            {matches.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => select(option.value)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-canvas',
                    option.value === value && 'font-medium text-brand-800',
                  )}
                  title={
                    option.marked && markLabel ? `${option.label} — ${markLabel}` : option.label
                  }
                >
                  {/*
                    Pladsholderen er ikke pynt: uden den ville de umarkerede navne staa et par
                    pixels til venstre for de markerede, og listen ville se sorteret ud paa noget
                    andet end det, prikken siger.
                  */}
                  {option.marked ? <Mark label={markLabel} /> : <span className="w-1.5 shrink-0" />}
                  <span className="truncate">{option.label}</span>
                </button>
              </li>
            ))}
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-xs text-ink-muted">Ingen træffere</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('field-label', className)} {...props} />;
}

export function Progress({ value, className }: { value: number; className?: string }) {
  const clamped = Math.max(0, Math.min(100, value));
  // Over 100 % vises som fuld bjaelke i advarselsfarve: overfakturering skal ses,
  // ikke skjules bag en bjaelke der ligner alle andre.
  const overBudget = value > 100;

  return (
    <div
      className={cn('h-2 w-full overflow-hidden rounded-full bg-canvas', className)}
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn('h-full rounded-full', overBudget ? 'bg-warning' : 'bg-brand')}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

const alertVariants = cva('rounded-md border px-3 py-2 text-sm', {
  variants: {
    tone: {
      info: 'border-blue-200 bg-blue-50 text-blue-900',
      success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      warning: 'border-amber-200 bg-amber-50 text-amber-900',
      error: 'border-red-200 bg-red-50 text-red-900',
    },
  },
  defaultVariants: { tone: 'info' },
});

export function Alert({
  className,
  tone,
  title,
  children,
  onDismiss,
  dismissLabel = 'Luk besked',
}: VariantProps<typeof alertVariants> & {
  className?: string;
  title?: string;
  children?: ReactNode;
  /**
   * Naar den er sat, vises en lukkeknap til hoejre. Uden den ser beskeden ud praecis som
   * foer - en besked der ikke kan lukkes skal ikke have en knap der antyder at den kan.
   */
  onDismiss?: () => void;
  dismissLabel?: string;
}) {
  const body = (
    <>
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={title ? 'mt-1' : undefined}>{children}</div> : null}
    </>
  );

  return (
    <div className={cn(alertVariants({ tone }), className)} role="status">
      {onDismiss ? (
        <div className="flex items-start gap-2">
          {/* min-w-0 saa lang tekst kan ombrydes i stedet for at skubbe knappen ud. */}
          <div className="min-w-0 flex-1">{body}</div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            title={dismissLabel}
            // Farven arves fra beskedens tone, saa knappen passer til alle fire varianter.
            className="-mr-1 shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : (
        body
      )}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-line', className)} />;
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-4 w-4 animate-spin text-ink-muted', className)} aria-hidden />;
}

/** Open modals. The page stays locked until the last one closes. */
let scrollLocks = 0;
let unlockedStyle: { overflow: string; paddingRight: string } | null = null;

/**
 * Stops the page behind an open modal from scrolling, so only the modal does.
 *
 * The page scrolls on the document, not in a container, so the lock is `overflow: hidden` on
 * body. Hiding the scrollbar would make the page jump sideways by its width; the same width is
 * added as padding while the lock holds.
 */
function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const body = document.body;
    if (scrollLocks === 0) {
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      unlockedStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
      body.style.overflow = 'hidden';
      if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    }
    scrollLocks += 1;

    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0 && unlockedStyle) {
        body.style.overflow = unlockedStyle.overflow;
        body.style.paddingRight = unlockedStyle.paddingRight;
        unlockedStyle = null;
      }
    };
  }, [active]);
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useScrollLock(open);

  if (!open) return null;

  return (
    // overscroll-contain: reaching the end of the modal must not hand the scroll on to the page.
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-ink/40 p-3 py-6 sm:p-4 sm:py-10">
      <div
        className={cn(
          'w-full rounded-lg bg-surface shadow-xl',
          size === 'lg' ? 'max-w-3xl' : 'max-w-lg',
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-start justify-between gap-2 border-b border-line px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{title}</h2>
            {description ? <p className="mt-1 text-xs text-ink-muted">{description}</p> : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={onClose}
            aria-label="Luk"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-4 py-4 sm:px-5">{children}</div>
        {footer ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line px-4 py-3 sm:px-5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function Table({
  className,
  stickyHeader = false,
  fill = false,
  ...props
}: HTMLAttributes<HTMLTableElement> & {
  /**
   * Lader tabellen fylde en forælder med fast hoejde og rulle resten selv.
   *
   * Til et kort i en gridraekke, hvis hoejde er sat og ikke kommer af indholdet - se
   * Dashboard.tsx. Klasserne skal paa *indpakningen* og ikke paa `<table>`, som `className`
   * rammer: det er indpakningen, der er flex-barnet, og en tabel kan ikke selv vaere en
   * rulleflade.
   *
   * `min-h-0` er den vaesentlige: et flex-barn maa som standard ikke blive lavere end sit
   * indhold, saa uden den ville en lang tabel skubbe kortet ud af raekken i stedet for at
   * rulle. Og fordi indpakningen saa er den naermeste rulleflade, virker Th's `sticky top-0`
   * af sig selv - hovedet bliver staaende, mens raekkerne ruller under det.
   */
  fill?: boolean;
  /**
   * Lader tabelhovedet klaebe under app-headeren (64 px), mens siden ruller - som
   * maerkatlinjen paa /fakturering.
   *
   * To ting saettes, og kun her - saa andre tabeller (fx /timer) er helt uroerte:
   *
   *   `overflow-x: clip` i stedet for den almindelige `overflow-x-auto`. Det er den ene
   *   vaerdi der baade fjerner den vandrette rullebjaelke *og* lader et sticky hoved virke:
   *   `auto`, `scroll` og `hidden` goer alle indpakningen til en rulleflade, der faelder
   *   sticky (hovedet ruller ud med siden), mens `clip` ikke er en rulleflade - saa hovedet
   *   maaler op mod selve siden. Prisen er, at en tabel bredere end skaermen bliver klippet
   *   frem for at kunne rulles vandret; bevidst her, hvor den vandrette scroll skal vaek.
   *
   *   `[&_th]:top-header` skubber hovedets offset fra Th's standard top-0 til de 64 px, saa
   *   det klaeber *under* app-headeren og ikke bag den. Descendant-reglen vinder over Th's
   *   egen top-0 paa specificitet, og rammer kun denne tabels th.
   *
   *   **Both only from `md` up.** On a phone, clipping would cut off the columns that do not
   *   fit, with no way to reach them - so the table scrolls sideways like the others, and the
   *   header does not stick. The offset goes with it: inside a scrolling wrapper, 64 px would
   *   push the header row down over the first rows.
   */
  stickyHeader?: boolean;
}) {
  return (
    <div
      className={cn(
        'w-full',
        stickyHeader
          ? 'overflow-x-auto md:overflow-x-clip md:[&_th]:top-header'
          : 'overflow-x-auto',
        fill && 'min-h-0 flex-1 overflow-y-auto',
      )}
    >
      <table className={cn('w-full border-collapse text-sm', className)} {...props} />
    </div>
  );
}

export function Th({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      // top-0 er standard. En sticky-hoved-tabel (Table stickyHeader) skubber offset til
      // top-header via en descendant-regel, saa kun den ene tabel klaeber under app-headeren.
      className={cn(
        'sticky top-0 z-10 border-b border-line bg-canvas px-3 py-2 text-left text-xs font-semibold text-ink-muted',
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('border-b border-line px-3 py-2 align-middle', className)} {...props} />;
}

/** Videresender ref'en, saa en fremhaevet raekke kan rulles ind i billedet. */
export const Tr = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(function Tr(
  { className, ...props },
  ref,
) {
  return <tr ref={ref} className={cn('hover:bg-canvas', className)} {...props} />;
});
